import { ActionIcon, Alert, Button, Card, Checkbox, Group, Popover, Stack, Tabs, Text } from "@mantine/core";
import { IconBookmark, IconClockHour4, IconFilter, IconPhone, IconRadar2, IconUserCheck } from "@tabler/icons-react";
import type { UseQueryResult } from "@tanstack/react-query";
import { EmptyState, FilterBar, LoadingOverlay } from "../ui";
import { ApiError } from "../../lib/apiClient";
import type { QueueBank, QueueMatch } from "../../lib/api";
import type { Job } from "../../types/api";
import { EXPLORE_SAVE_FORWARD_LIMIT } from "./constants";
import { exploreQueueEmptyCopy, saveForwardLabel } from "./exploreUtils";
import { MatchCard } from "./MatchCard";

export function ExploreWorkspace({
  tab,
  onTabChange,
  mustHaveOnly,
  callableOnly,
  withinBudgetOnly,
  matchingLocationsOnly,
  onMustHaveOnly,
  onCallableOnly,
  onWithinBudgetOnly,
  onMatchingLocationsOnly,
  queueQuery,
  cooldownQuery,
  ownedQuery,
  savedQuery,
  selectedJob,
  callingOpen,
  claimingId,
  savingId,
  passingId,
  onCall,
  onSaveForward,
  onPass,
  onResumeCall,
}: {
  tab: string | null;
  onTabChange: (value: string | null) => void;
  mustHaveOnly: boolean;
  callableOnly: boolean;
  withinBudgetOnly: boolean;
  matchingLocationsOnly: boolean;
  onMustHaveOnly: (value: boolean) => void;
  onCallableOnly: (value: boolean) => void;
  onWithinBudgetOnly: (value: boolean) => void;
  onMatchingLocationsOnly: (value: boolean) => void;
  queueQuery: UseQueryResult<QueueMatch[]>;
  cooldownQuery: UseQueryResult<QueueMatch[]>;
  ownedQuery: UseQueryResult<QueueMatch[]>;
  savedQuery: UseQueryResult<QueueBank>;
  selectedJob: Job | undefined;
  callingOpen: boolean;
  claimingId: string | undefined;
  savingId: string | undefined;
  passingId: string | undefined;
  onCall: (match: QueueMatch) => void;
  onSaveForward: (match: QueueMatch) => void;
  onPass: (match: QueueMatch) => void;
  onResumeCall: (match: QueueMatch) => void;
}) {
  const queue = queueQuery.data ?? [];
  const current = queue[0];
  const savedBank = savedQuery.data;
  const savedCount = savedBank?.saved_count ?? 0;
  const savedLimit = savedBank?.saved_limit ?? EXPLORE_SAVE_FORWARD_LIMIT;
  const bankFull = savedCount >= savedLimit;
  const { emptyTitle, emptyDescription } = exploreQueueEmptyCopy(mustHaveOnly, callableOnly, savedCount);
  const activeFilterCount = [mustHaveOnly, callableOnly, withinBudgetOnly, matchingLocationsOnly].filter(
    Boolean,
  ).length;

  return (
    <Tabs value={tab} onChange={onTabChange}>
      <FilterBar
        actions={
          <Popover position="bottom-end" shadow="md" withinPortal>
            <Popover.Target>
              <ActionIcon
                variant={activeFilterCount > 0 ? "filled" : "default"}
                color={activeFilterCount > 0 ? "violet" : "gray"}
                size="lg"
                aria-label="Filters"
              >
                <IconFilter size="var(--axio-unit-18)" />
              </ActionIcon>
            </Popover.Target>
            <Popover.Dropdown>
              <Stack gap="md">
                <Checkbox
                  label="Must-have skills only"
                  checked={mustHaveOnly}
                  onChange={(e) => onMustHaveOnly(e.currentTarget.checked)}
                />
                <Checkbox
                  label="Callable profiles only"
                  checked={callableOnly}
                  onChange={(e) => onCallableOnly(e.currentTarget.checked)}
                />
                <Checkbox
                  label="Within budget only"
                  checked={withinBudgetOnly}
                  onChange={(e) => onWithinBudgetOnly(e.currentTarget.checked)}
                />
                <Checkbox
                  label="Matching locations only"
                  checked={matchingLocationsOnly}
                  onChange={(e) => onMatchingLocationsOnly(e.currentTarget.checked)}
                />
              </Stack>
            </Popover.Dropdown>
          </Popover>
        }
      >
        <Button
          variant={tab === "queue" ? "filled" : "default"}
          leftSection={<IconPhone size="var(--axio-unit-16)" />}
          onClick={() => onTabChange("queue")}
        >
          Queue
        </Button>
        <Button
          variant={tab === "saved" ? "filled" : "default"}
          leftSection={<IconBookmark size="var(--axio-unit-16)" />}
          onClick={() => onTabChange("saved")}
        >
          Saved {savedCount}/{savedLimit}
        </Button>
        <Button
          variant={tab === "cooldown" ? "filled" : "default"}
          leftSection={<IconClockHour4 size="var(--axio-unit-16)" />}
          onClick={() => onTabChange("cooldown")}
        >
          Cooldown
        </Button>
        <Button
          variant={tab === "owned" ? "filled" : "default"}
          leftSection={<IconUserCheck size="var(--axio-unit-16)" />}
          onClick={() => onTabChange("owned")}
        >
          My follow-ups / owned
        </Button>
      </FilterBar>

      <Tabs.Panel value="queue" pt="md">
        <Stack gap="var(--axio-unit-32)">
          {bankFull && (
            <Alert color="yellow" title="Save-forward bank is full">
              Call or Pass someone in Saved before skipping ahead again.
            </Alert>
          )}
          {queueQuery.isLoading ? (
            <LoadingOverlay />
          ) : queueQuery.isError ? (
            <EmptyState
              icon={IconRadar2}
              title="Could not load queue"
              description={
                queueQuery.error instanceof ApiError
                  ? queueQuery.error.message
                  : "Check your KYC and calling seat, then retry."
              }
              action={<Button onClick={() => void queueQuery.refetch()}>Retry</Button>}
            />
          ) : current == null ? (
            <EmptyState icon={IconPhone} title={emptyTitle} description={emptyDescription} />
          ) : (
            <MatchCard
              match={current}
              job={selectedJob}
              detailed
              onCall={onCall}
              onSaveForward={onSaveForward}
              onPass={onPass}
              calling={claimingId === current.id}
              saveForwardLoading={savingId === current.id}
              saveForwardDisabled={bankFull}
              saveForwardLabel={saveForwardLabel(savedCount, savedLimit)}
              passLoading={passingId === current.id}
              callLocked={!callingOpen}
            />
          )}
        </Stack>
      </Tabs.Panel>

      <Tabs.Panel value="saved" pt="md">
        {savedQuery.isLoading ? (
          <LoadingOverlay />
        ) : savedQuery.isError ? (
          <EmptyState
            icon={IconBookmark}
            title="Could not load saved candidates"
            description="Please retry."
            action={<Button onClick={() => void savedQuery.refetch()}>Retry</Button>}
          />
        ) : (
          <Stack gap="var(--axio-unit-32)">
            {(savedBank?.items ?? []).map((m) => (
              <MatchCard
                key={m.id}
                match={m}
                job={selectedJob}
                detailed
                onCall={onCall}
                onPass={onPass}
                calling={claimingId === m.id}
                passLoading={passingId === m.id}
                callLocked={!callingOpen}
              />
            ))}
            {(savedBank?.items ?? []).length === 0 && (
              <EmptyState
                icon={IconBookmark}
                title="Nothing saved"
                description={`Save forward parks up to ${savedLimit} people without revealing contact.`}
              />
            )}
          </Stack>
        )}
      </Tabs.Panel>

      <Tabs.Panel value="cooldown" pt="md">
        {cooldownQuery.isLoading ? (
          <LoadingOverlay />
        ) : cooldownQuery.isError ? (
          <EmptyState icon={IconClockHour4} title="Could not load cooldown" description="Please retry." action={<Button onClick={() => void cooldownQuery.refetch()}>Retry</Button>} />
        ) : (
          <Stack gap="var(--axio-unit-32)">
            {(cooldownQuery.data ?? []).map((m) => (
              <MatchCard key={m.id} match={m} job={selectedJob} onCall={() => undefined} dimmed />
            ))}
            {(cooldownQuery.data ?? []).length === 0 && (
              <Text c="dimmed">Nothing in cooldown.</Text>
            )}
          </Stack>
        )}
      </Tabs.Panel>

      <Tabs.Panel value="owned" pt="md">
        {ownedQuery.isLoading ? (
          <LoadingOverlay />
        ) : ownedQuery.isError ? (
          <EmptyState icon={IconUserCheck} title="Could not load owned candidates" description="Please retry." action={<Button onClick={() => void ownedQuery.refetch()}>Retry</Button>} />
        ) : (
          <Stack gap="var(--axio-unit-32)">
            {(ownedQuery.data ?? []).map((m) => (
              <Card
                key={m.id}
                withBorder
                padding="lg"
                role="group"
                aria-label={`Owned candidate: ${m.candidate?.full_name ?? "Candidate"}`}
              >
                <Group justify="space-between">
                  <div>
                    <Text fw="var(--axio-font-weight-semibold)">{m.candidate?.full_name}</Text>
                    <Text size="sm" c="dimmed">
                      {m.status}
                      {m.follow_up_expires_at
                        ? ` · follow-up until ${new Date(m.follow_up_expires_at).toLocaleString()}`
                        : ""}
                      {m.submission_id ? ` · submission ${m.submission_id.slice(0, 8)}…` : ""}
                    </Text>
                  </div>
                  {(m.status === "follow_up" || m.status === "locked") && (
                    <Button size="xs" onClick={() => onResumeCall(m)}>
                      Resume / log call
                    </Button>
                  )}
                </Group>
              </Card>
            ))}
            {(ownedQuery.data ?? []).length === 0 && (
              <Text c="dimmed">No owned or follow-up candidates.</Text>
            )}
          </Stack>
        )}
      </Tabs.Panel>
    </Tabs>
  );
}
